import { computed, toValue } from 'vue'
import {
  calculateDisplayPrice,
  calculateOriginalPrice,
  getSortedProductTags,
  normalizeProductData,
} from '@/utils/product'
import { resolveProductModelNo, resolveProductName } from '~/utils/tracking/helpers'

/**
 * @description 商品数据处理 Hook，统一处理价格、标签、ID等逻辑
 * @param {Object|import('vue').MaybeRefOrGetter<Object>} productSource - 原始商品对象或 getter
 * @param {boolean} [isActivityEntry=false] - 是否为活动入口；为 true 时 image 使用活动图优先链路，否则仅用 pic
 * @returns {Object} 处理后的商品数据和状态
 */
export const useProductData = (productSource, isActivityEntry = false) => {
  const product = computed(() => toValue(productSource))

  // 1. 归一化商品数据 (处理秒杀搜索 VO 等)
  const normalizedProduct = computed(() => normalizeProductData(product.value))

  // 2. 基础信息
  const productId = computed(() => product.value?.prodId)
  const modelNo = computed(() => resolveProductModelNo(product.value))
  const name = computed(() => resolveProductName(product.value))
  const description = computed(() => product.value?.brief || '')
  const image = computed(() =>
    isActivityEntry
      ? product.value?.activityImage || product.value?.pic || ''
      : product.value?.pic || ''
  )

  // 3. 价格计算
  const displayPrice = computed(() => calculateDisplayPrice(normalizedProduct.value, null))
  const originalPrice = computed(() => calculateOriginalPrice(normalizedProduct.value, null))

  // 4. 标签处理
  const tags = computed(() => {
    const p = product.value
    const rawTags = p?.labelList || p?.tags || []
    if (rawTags.length === 0 && p?.tag) {
      return [p.tag]
    }
    return getSortedProductTags(rawTags)
  })

  const hasTags = computed(() => tags.value.length > 0)

  // 5. 详情跳转链接
  const detailTo = computed(() => {
    if (!productId.value) return '/store/products'
    const base = `/store/products/${productId.value}`
    const q = product.value?.storeDetailQuery
    if (q && typeof q === 'object') {
      const sp = new URLSearchParams()
      Object.entries(q).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return
        sp.set(k, String(v))
      })
      const qs = sp.toString()
      return qs ? `${base}?${qs}` : base
    }
    return base
  })

  return {
    normalizedProduct,
    productId,
    modelNo,
    name,
    description,
    image,
    displayPrice,
    originalPrice,
    tags,
    hasTags,
    detailTo,
  }
}
